import { useRef, useState } from 'react';
import { VegaFlex, VegaPageNotification } from '@globalpayments/vega-react';
import { getDetailsEndpoint, getFilterEndpoint } from '../../api/kirbyFiltering';
import PageSkeleton from '../../components/pageSkeleton';
import {
  useAvailableFilterTypes,
  useExecuteDetails,
  useExecuteFilter,
  useFilterPropertyDefinitions,
} from '../../hooks/useKirbyFiltering';
import {
  KirbyFilterClauseNode,
  KirbyDetailsIdentifier,
  KirbyFilterDto,
  KirbyFilterGroupNode,
  KirbyFilterNode,
  KirbyFilterRequest,
  KirbyFilterValidationIssue,
} from '../../types/kirbyFiltering';
import {
  removeFilterNode,
  serializeFilterDraft,
  updateFilterNode,
  validateFilterDraft,
} from '../../utility/kirbyFiltering';
import FilterBuilder from './components/FilterBuilder';
import FilterDetailsModal from './components/FilterDetailsModal';
import FilterResults from './components/FilterResults';

const createClause = (id: number): KirbyFilterClauseNode => ({
  kind: 'clause',
  id: `clause-${id}`,
  property: '',
  operator: '',
  value: '',
});

const createGroup = (id: number, children: KirbyFilterNode[] = []): KirbyFilterGroupNode => ({
  kind: 'group',
  id: `group-${id}`,
  conjunction: 'And',
  children,
});

interface DetailsTarget {
  id: KirbyDetailsIdentifier;
}

const defaultPageSize = 20;

const summarizeFilter = (filter: KirbyFilterDto | null): string => {
  if (!filter) return 'All records';
  if ('Clause' in filter) {
    const { Property, Operator, Value } = filter.Clause;
    return Value ? `${Property} ${Operator} ${Value}` : `${Property} ${Operator}`;
  }

  return filter.Children.map(summarizeFilter).join(` ${filter.Conjunction} `);
};

/** Provides the metadata-driven Kirby filtering workflow. */
const Filtering = () => {
  const nextNodeId = useRef(1);
  const [filterType, setFilterType] = useState('');
  const [root, setRoot] = useState<KirbyFilterGroupNode>(() => createGroup(0));
  const [issues, setIssues] = useState<KirbyFilterValidationIssue[]>([]);
  const [submittedRequest, setSubmittedRequest] = useState<KirbyFilterRequest>();
  const [isBuilderExpanded, setIsBuilderExpanded] = useState(true);
  const [detailsTarget, setDetailsTarget] = useState<DetailsTarget>();
  const [requestReason, setRequestReason] = useState('');
  const availableTypesQuery = useAvailableFilterTypes();
  const definitionsQuery = useFilterPropertyDefinitions(filterType);
  const executeMutation = useExecuteFilter();
  const detailsMutation = useExecuteDetails();
  const definitions = definitionsQuery.data ?? [];

  const resetDraft = () => {
    setFilterType('');
    setIsBuilderExpanded(true);
    setRoot(createGroup(0));
    setIssues([]);
    setSubmittedRequest(undefined);
    executeMutation.reset();
    detailsMutation.reset();
    setDetailsTarget(undefined);
    setRequestReason('');
  };

  const handleFilterTypeChange = (selectedFilterType: string) => {
    setFilterType(selectedFilterType);
    setIsBuilderExpanded(true);
    setRoot(createGroup(0));
    setIssues([]);
    setSubmittedRequest(undefined);
    executeMutation.reset();
    detailsMutation.reset();
    setDetailsTarget(undefined);
    setRequestReason('');
  };

  const handleNodeChange = (nodeId: string, update: (node: KirbyFilterNode) => KirbyFilterNode) => {
    setRoot((currentRoot) => updateFilterNode(currentRoot, nodeId, update));
    setIssues((currentIssues) => currentIssues.filter((issue) => issue.nodeId !== nodeId));
  };

  const handleSubmit = () => {
    const draft = { filterType, root };
    const validationIssues = root.children.length === 0 ? [] : validateFilterDraft(draft, definitions);
    setIssues(validationIssues);
    if (validationIssues.length > 0) return;

    const request = serializeFilterDraft(draft, 1, defaultPageSize);
    setSubmittedRequest(request);
    setIsBuilderExpanded(false);
    executeMutation.mutate({ filterType, request });
  };

  const handlePageChange = (pageNumber: number) => {
    if (!submittedRequest || !Number.isSafeInteger(pageNumber) || pageNumber < 1) return;

    const request = { ...submittedRequest, PageNumber: pageNumber };
    setSubmittedRequest(request);
    executeMutation.mutate({ filterType, request });
  };

  const handlePageSizeChange = (pageSize: number) => {
    if (!submittedRequest || !Number.isSafeInteger(pageSize) || pageSize < 1) return;

    const request = { ...submittedRequest, PageNumber: 1, PageSize: pageSize };
    setSubmittedRequest(request);
    executeMutation.mutate({ filterType, request });
  };

  const handleOpenDetails = (id: KirbyDetailsIdentifier) => {
    detailsMutation.reset();
    setRequestReason('');
    setDetailsTarget({ id });
  };

  const handleCloseDetails = () => {
    detailsMutation.reset();
    setDetailsTarget(undefined);
    setRequestReason('');
  };

  const handleRequestDetails = (reason: string) => {
    if (!detailsTarget) return;
    detailsMutation.mutate({ filterType, id: detailsTarget.id, requestReason: reason });
  };

  if (availableTypesQuery.isLoading) return <PageSkeleton label="Loading filter categories" variant="form" />;
  if (availableTypesQuery.isError) {
    return (
      <VegaPageNotification
        duration={0}
        message="Filter categories could not be loaded from Kirby."
        notificationTitle="Filtering unavailable"
        type="error"
      />
    );
  }

  const endpoint = filterType ? getFilterEndpoint(filterType) : undefined;
  const detailsEndpoint = filterType ? getDetailsEndpoint(filterType) : undefined;
  const submissionUnavailableMessage =
    filterType && !endpoint
      ? `${filterType} is discoverable, but its result endpoint has not been verified.`
      : undefined;
  const querySummary = submittedRequest
    ? `${filterType}: ${summarizeFilter(submittedRequest.Filter)} | Page ${submittedRequest.PageNumber}, ${submittedRequest.PageSize} per page`
    : 'Build a query';

  return (
    <VegaFlex direction="col" gap="size-24">
      <FilterBuilder
        availableFilterTypes={availableTypesQuery.data ?? []}
        definitions={definitions}
        filterType={filterType}
        isExpanded={isBuilderExpanded}
        isExecuting={executeMutation.isPending}
        issues={issues}
        onAddClause={(group) =>
          handleNodeChange(group.id, (currentGroup) =>
            currentGroup.kind === 'group'
              ? { ...currentGroup, children: [...currentGroup.children, createClause(nextNodeId.current++)] }
              : currentGroup,
          )
        }
        onAddGroup={(group) => {
          const groupId = nextNodeId.current++;
          const clauseId = nextNodeId.current++;
          handleNodeChange(group.id, (currentGroup) =>
            currentGroup.kind === 'group'
              ? {
                  ...currentGroup,
                  children: [...currentGroup.children, createGroup(groupId, [createClause(clauseId)])],
                }
              : currentGroup,
          );
        }}
        onFilterTypeChange={handleFilterTypeChange}
        onNodeChange={handleNodeChange}
        onExpand={setIsBuilderExpanded}
        onRemoveNode={(nodeId) => setRoot((currentRoot) => removeFilterNode(currentRoot, nodeId))}
        onReset={resetDraft}
        onSubmit={handleSubmit}
        root={root}
        querySummary={querySummary}
        submissionUnavailableMessage={submissionUnavailableMessage}
      />
      {definitionsQuery.isLoading && <PageSkeleton label="Loading property definitions" variant="form" />}
      {definitionsQuery.isError && (
        <VegaPageNotification
          duration={0}
          message="Property definitions could not be loaded for the selected category."
          notificationTitle="Properties unavailable"
          type="error"
        />
      )}
      <FilterResults
        data={executeMutation.data}
        filterType={filterType}
        hasSubmitted={executeMutation.isSuccess}
        isError={executeMutation.isError}
        isLoading={executeMutation.isPending}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onRequestDetails={detailsEndpoint ? handleOpenDetails : undefined}
      />
      <FilterDetailsModal
        details={detailsMutation.data}
        filterType={filterType}
        isError={detailsMutation.isError}
        isLoading={detailsMutation.isPending}
        onClose={handleCloseDetails}
        onRequestDetails={handleRequestDetails}
        open={Boolean(detailsTarget)}
        recordId={detailsTarget?.id}
        requestReason={requestReason}
        setRequestReason={setRequestReason}
      />
    </VegaFlex>
  );
};

export default Filtering;
